// ============================================================
//  src/pages/bots/tabs/WhatsAppAdminTab.js
//  "Phone Admin" — link the owner's WhatsApp so they can run the
//  bot from their phone (/stats, /reply, /broadcast, /pause …).
//  Backend: api/bots/whatsapp-admin + engine/Runtime/OwnerAdmin.php
//  Styles: ./WhatsAppAdminTab.css (scoped under .wad-*)
// ============================================================
import React, { useCallback, useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { getWhatsAppAdmin, whatsAppAdminAction } from '../../../api/client';
import './WhatsAppAdminTab.css';

// Quality-rating alerts and the 8am summary are sent by cron/owner_admin.php (every 15 min).
const COMMANDS = [
  { group: 'Numbers', items: [
    { cmd: '/stats',      icon: 'fa-chart-simple',      desc: 'Users, messages, paid orders and errors', eg: '/stats 7d' },
    { cmd: '/health',     icon: 'fa-heart-pulse',       desc: 'Quality rating, messaging limit, webhook and billing' },
    { cmd: '/errors',     icon: 'fa-triangle-exclamation', desc: 'Last 5 errors in plain English' },
  ] },
  { group: 'Support tickets', items: [
    { cmd: '/queue',       icon: 'fa-ticket',            desc: 'Every open ticket: waiting, yours and other admins’', eg: '/mine' },
    { cmd: '/take 14',     icon: 'fa-hand',              desc: 'Claim a ticket — only you get that customer’s messages' },
    { cmd: '/assign 14 …', icon: 'fa-share',             desc: 'Hand a ticket to another admin (pick from a list)', eg: '/assign 14 Musa' },
    { cmd: '/reply 14 …',  icon: 'fa-reply',             desc: 'Answer the customer — claims the ticket if nobody has', eg: '/done 14' },
    { cmd: '/release 14',  icon: 'fa-rotate-left',       desc: 'Put a ticket back in the queue for someone else' },
    { cmd: '/admins',      icon: 'fa-user-group',        desc: 'Linked admins and how many tickets each has' },
  ] },
  { group: 'Commands', items: [
    { cmd: '/test on|off',   icon: 'fa-flask',           desc: 'On: your phone runs unpublished drafts. Customers keep the live version', eg: '/test status' },
    { cmd: '/versions …',    icon: 'fa-clock-rotate-left', desc: 'Every published version of a command, newest first', eg: '/versions /price' },
    { cmd: '/undo …',        icon: 'fa-rotate-left',     desc: 'Put the previous version back live (asks to confirm)', eg: '/undo /price' },
  ] },
  { group: 'Customers', items: [
    { cmd: '/user 0803…', icon: 'fa-user',              desc: 'Profile, activity and recent orders for one customer' },
    { cmd: '/block 14',   icon: 'fa-ban',               desc: 'Block or /unblock a customer' },
  ] },
  { group: 'Messaging', items: [
    { cmd: '/broadcast …', icon: 'fa-bullhorn',         desc: 'Preview, then send to last-24h users', eg: '/broadcast all …' },
    { cmd: '/broadcasts',  icon: 'fa-chart-bar',        desc: 'Progress of recent broadcasts, with pause and stop' },
    { cmd: '/scheduled',   icon: 'fa-calendar-days',    desc: 'Upcoming scheduled messages', eg: '/scheduled cancel 1' },
    { cmd: '/pause …',     icon: 'fa-circle-pause',     desc: 'Stop all replies, optional away note', eg: '/resume' },
  ] },
  { group: 'You', items: [
    { cmd: '/alerts on|off',  icon: 'fa-bell',          desc: 'Handoff and quality-rating alerts' },
    { cmd: '/summary on|off', icon: 'fa-sun',           desc: "Yesterday's numbers every morning at 8am" },
    { cmd: '/admin',          icon: 'fa-screwdriver-wrench', desc: 'Menu with quick buttons' },
    { cmd: '/guide',          icon: 'fa-book-open',     desc: 'What every command does, with examples', eg: '/guide assign' },
  ] },
];

function ago(dt) {
  if (!dt) return 'never';
  const s = Math.floor((Date.now() - new Date(String(dt).replace(' ', 'T'))) / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}
function initials(name = '') {
  return name.split(' ').map(p => p[0] ?? '').join('').toUpperCase().slice(0, 2) || '#';
}
async function copy(text, label = 'Copied') {
  try { await navigator.clipboard.writeText(text); toast.success(label); }
  catch { toast.error('Copy failed — please copy it manually'); }
}

// ── Phone preview (illustration only) ─────────────────────────
function PhonePreview({ botName }) {
  return (
    <div className="wad-phone" aria-hidden="true">
      <div className="wad-phone-top">
        <span className="wad-phone-av"><i className="fa-solid fa-robot" /></span>
        <div><b>{botName || 'Your bot'}</b><span>online</span></div>
      </div>
      <div className="wad-phone-body">
        <div className="wad-b out">/stats</div>
        <div className="wad-b in">
          <b>📊 {botName || 'Your bot'}</b>
          <em>Today · so far</em>
          <span>👥 Users: <b>1,204</b> · +18 new</span>
          <span>💬 Messages in: <b>412</b></span>
          <span>🧾 Paid orders: <b>23</b> · ₦184,500</span>
          <span>🙋 Waiting for a human: <b>2</b></span>
        </div>
        <div className="wad-b in alert">🙋 <b>Tunde</b> wants a human<br /><em>“My data didn't drop”</em></div>
        <div className="wad-b out quote"><span className="wad-q">Tunde wants a human</span>Sorted, check now 👍</div>
      </div>
    </div>
  );
}

export default function WhatsAppAdminTab({ bId, bot }) {
  const [state, setState]     = useState(null);
  const [loading, setLoading] = useState(true);
  const [denied, setDenied]   = useState('');
  const [busy, setBusy]       = useState('');
  const [left, setLeft]       = useState(0);
  const [away, setAway]       = useState('');
  const [confirmId, setConfirmId] = useState(null);
  const adminCount = useRef(0);

  const apply = useCallback((d, { announce = false } = {}) => {
    if (announce && d.admins.length > adminCount.current) {
      toast.success('Phone linked! Check WhatsApp for your admin menu.');
    }
    adminCount.current = d.admins.length;
    setState(d);
    setLeft(d.link?.expiresIn ?? 0);
  }, []);

  const load = useCallback(async (opts) => {
    try {
      const { data } = await getWhatsAppAdmin(bId);
      if (data?.success) {
        apply(data, opts);
        if (!opts?.announce) setAway(data.awayMessage || '');
      }
    } catch (err) {
      if (err?.response?.status === 403) setDenied(err.response.data?.message || 'Only the bot owner can manage this.');
      else toast.error('Could not load Phone Admin settings');
    }
    setLoading(false);
  }, [bId, apply]);

  useEffect(() => { load(); }, [load]);

  // Countdown + poll for the new admin while a code is live
  const codeLive = !!state?.link && left > 0;
  useEffect(() => {
    if (!codeLive) return undefined;
    const tick = setInterval(() => setLeft(l => Math.max(0, l - 1)), 1000);
    const poll = setInterval(() => load({ announce: true }), 4000);
    return () => { clearInterval(tick); clearInterval(poll); };
  }, [codeLive, load]);

  async function act(action, data = {}, key = action) {
    setBusy(key);
    try {
      const { data: res } = await whatsAppAdminAction(bId, action, data);
      if (res?.success) { apply(res); return res; }
      toast.error(res?.message || 'Something went wrong');
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Something went wrong');
    } finally {
      setBusy('');
    }
    return null;
  }

  async function savePause(paused) {
    const res = await act('set_pause', { paused, awayMessage: away.trim() }, 'pause');
    if (res) toast.success(paused ? 'Bot paused — it won’t reply to anyone' : 'Bot is live again');
  }

  if (loading) {
    return (
      <div className="wad">
        <div className="wad-card wad-skel"><span className="sk w40" /><span className="sk w70" /><span className="sk w60" /></div>
      </div>
    );
  }

  if (denied) {
    return (
      <div className="wad">
        <div className="wad-card wad-denied">
          <span className="wad-ic"><i className="fa-solid fa-lock" /></span>
          <b>Owner only</b>
          <p>{denied} Ask the bot owner to link their phone.</p>
        </div>
      </div>
    );
  }

  const admins = state?.admins ?? [];
  const full = admins.length >= (state?.maxAdmins ?? 5);
  const mins = Math.floor(left / 60), secs = String(left % 60).padStart(2, '0');
  const command = state?.link ? `/admin ${state.link.code}` : '';

  return (
    <div className="wad">
      {/* ── Hero ── */}
      <section className="wad-hero">
        <div className="wad-hero-copy">
          <span className="wad-eyebrow"><i className="fa-brands fa-whatsapp" /> Phone Admin</span>
          <h2>Run {bot?.bName || 'your bot'} from WhatsApp</h2>
          <p>Link your phone once, then chat with your own bot to check today’s numbers, answer customers who need a human, send a broadcast or pause everything — without opening the dashboard.</p>
          <div className="wad-hero-pills">
            <span className={`wad-status ${state?.paused ? 'is-paused' : 'is-live'}`}>
              <i className={`fa-solid ${state?.paused ? 'fa-circle-pause' : 'fa-circle'}`} />
              {state?.paused ? `Paused ${ago(state.pausedAt)}` : 'Bot is live'}
            </span>
            <span className="wad-count"><i className="fa-solid fa-mobile-screen" /> {admins.length} of {state?.maxAdmins ?? 5} phones linked</span>
          </div>
        </div>
        <PhonePreview botName={bot?.bName} />
      </section>

      <div className="wad-grid">
        <div className="wad-col">
          {/* ── Link ── */}
          <section className="wad-card">
            <div className="wad-card-head">
              <span className="wad-ic"><i className="fa-solid fa-link" /></span>
              <div>
                <h3>Link a phone</h3>
                <p>Send a one-time code to your bot from the WhatsApp you want to use.</p>
              </div>
            </div>

            {codeLive ? (
              <div className="wad-code">
                <div className="wad-code-digits" aria-label={`Code ${state.link.code}`}>
                  {state.link.code.split('').map((d, i) => <span key={i}>{d}</span>)}
                </div>
                <ol className="wad-code-steps">
                  <li>Open WhatsApp on the phone you want to link.</li>
                  <li>Send <code>{command}</code> to <b>{state.botPhone ? `+${state.botPhone}` : 'your bot’s number'}</b>.</li>
                  <li>You’ll get the admin menu straight back. This page updates by itself.</li>
                </ol>
                <div className="wad-code-actions">
                  {state.link.waLink && (
                    <a className="wad-btn primary" href={state.link.waLink} target="_blank" rel="noreferrer">
                      <i className="fa-brands fa-whatsapp" /> Open WhatsApp
                    </a>
                  )}
                  <button type="button" className="wad-btn ghost" onClick={() => copy(command, 'Command copied')}>
                    <i className="fa-regular fa-copy" /> Copy {command}
                  </button>
                </div>
                <div className="wad-code-foot">
                  <span className="wad-waiting"><span className="wad-dot" /> Waiting for your message · expires in {mins}:{secs}</span>
                  <button type="button" className="wad-link" onClick={() => act('cancel_code')} disabled={busy === 'cancel_code'}>Cancel</button>
                </div>
              </div>
            ) : (
              <div className="wad-link-cta">
                <button type="button" className="wad-btn primary lg" onClick={() => act('create_code')} disabled={full || busy === 'create_code'}>
                  {busy === 'create_code' ? <><span className="wad-spin" /> Creating code…</> : <><i className="fa-solid fa-plus" /> {admins.length ? 'Link another phone' : 'Link my phone'}</>}
                </button>
                <span>{full ? 'You’ve reached the limit. Remove a phone to add another.' : 'Codes work once and expire after 10 minutes.'}</span>
              </div>
            )}

            {admins.length > 0 && (
              <div className="wad-admins">
                {admins.map(a => (
                  <div key={a.id} className="wad-admin">
                    <span className="wad-av">{initials(a.name || '')}</span>
                    <div className="wad-admin-who">
                      <b>{a.name || a.phone}</b>
                      <span>{a.phone} · active {ago(a.lastSeenAt)}</span>
                    </div>
                    {confirmId === a.id ? (
                      <span className="wad-confirm">
                        Unlink?
                        <button type="button" className="wad-btn sm danger" onClick={async () => { await act('remove', { adminId: a.id }, 'remove'); setConfirmId(null); }} disabled={busy === 'remove'}>Unlink</button>
                        <button type="button" className="wad-btn sm ghost" onClick={() => setConfirmId(null)}>Cancel</button>
                      </span>
                    ) : (
                      <div className="wad-admin-actions">
                        <label className="wad-toggle" title="Handoff alerts on this phone">
                          <input type="checkbox" checked={a.alerts} onChange={e => act('set_alerts', { adminId: a.id, notify: e.target.checked }, `alerts-${a.id}`)} />
                          <span className="wad-switch" />
                          <span className="wad-toggle-label"><i className="fa-solid fa-bell" /> Alerts</span>
                        </label>
                        <label className="wad-toggle" title="Yesterday's numbers at 8am">
                          <input type="checkbox" checked={a.summary !== false} onChange={e => act('set_summary', { adminId: a.id, on: e.target.checked }, `summary-${a.id}`)} />
                          <span className="wad-switch" />
                          <span className="wad-toggle-label"><i className="fa-solid fa-sun" /> 8am summary</span>
                        </label>
                        <button type="button" className="wad-icon-btn" onClick={() => setConfirmId(a.id)} aria-label={`Unlink ${a.phone}`}>
                          <i className="fa-solid fa-link-slash" />
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* ── Pause ── */}
          <section className={`wad-card wad-pause ${state?.paused ? 'is-paused' : ''}`}>
            <div className="wad-card-head">
              <span className="wad-ic"><i className={`fa-solid ${state?.paused ? 'fa-circle-pause' : 'fa-circle-play'}`} /></span>
              <div>
                <h3>{state?.paused ? 'Bot is paused' : 'Pause replies'}</h3>
                <p>{state?.paused
                  ? 'Nobody gets automated replies. Messages still arrive in your Inbox.'
                  : 'Closed for the night or fixing something? Stop all replies without turning the bot off.'}</p>
              </div>
            </div>
            <label className="wad-label" htmlFor="wad-away">Away message <em>optional · sent once per customer</em></label>
            <textarea id="wad-away" className="wad-input" rows={2} maxLength={1000} value={away}
              placeholder="e.g. We’re closed for today 🌙 We’ll reply from 8am."
              onChange={e => setAway(e.target.value)} />
            <div className="wad-pause-actions">
              {state?.paused ? (
                <>
                  <button type="button" className="wad-btn ghost" onClick={() => savePause(true)} disabled={busy === 'pause'}>Save message</button>
                  <button type="button" className="wad-btn primary" onClick={() => savePause(false)} disabled={busy === 'pause'}>
                    <i className="fa-solid fa-play" /> Resume bot
                  </button>
                </>
              ) : (
                <button type="button" className="wad-btn dark" onClick={() => savePause(true)} disabled={busy === 'pause'}>
                  <i className="fa-solid fa-pause" /> Pause bot
                </button>
              )}
            </div>
          </section>
        </div>

        {/* ── Commands ── */}
        <aside className="wad-card wad-cmds">
          <div className="wad-card-head">
            <span className="wad-ic dark"><i className="fa-solid fa-terminal" /></span>
            <div>
              <h3>Commands</h3>
              <p>Only linked phones can use these. Everyone else just talks to your bot as normal.</p>
            </div>
          </div>
          {COMMANDS.map(g => (
            <div key={g.group} className="wad-cmd-group">
              <h4>{g.group}</h4>
              <ul>
                {g.items.map(c => (
                  <li key={c.cmd}>
                    <span className="wad-cmd-ic"><i className={`fa-solid ${c.icon}`} /></span>
                    <div>
                      <code>{c.cmd}</code>
                      <span>{c.desc}{c.eg && <> · <em>{c.eg}</em></>}</span>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          ))}
          <div className="wad-tip">
            <i className="fa-solid fa-reply" />
            <span><b>Swipe-reply to any alert</b> and your message goes straight to that customer.</span>
          </div>
          <div className="wad-note">
            <i className="fa-solid fa-circle-info" />
            <span>WhatsApp only lets a business message you within 24 hours of your last message. Tapping a button on the morning summary keeps alerts flowing — if they stop, send your bot <code>/stats</code>.</span>
          </div>
        </aside>
      </div>
    </div>
  );
}